import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowUpRight, Menu, X, Instagram, Facebook } from "lucide-react";
import { NAV_LINKS, SERVICES, COMPANY } from "@/data/site";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <>
      <header
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-navy-deep/80 backdrop-blur-xl border-b border-white/5"
            : "bg-navy-deep/30 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="max-w-8xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 shrink-0" aria-label="Click World Online — Home">
            <span className="bg-white rounded-full px-3 py-1.5 flex items-center">
              <img src="/assets/logo.png" alt="Click World Online logo" className="h-8 w-auto" />
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV_LINKS.map((link) =>
              link.dropdown ? (
                <div
                  key={link.to}
                  className="relative"
                  onMouseEnter={() => setDropOpen(true)}
                  onMouseLeave={() => setDropOpen(false)}
                >
                  <NavLink
                    to={link.to}
                    data-testid="nav-services-link"
                    className={({ isActive }) =>
                      `flex items-center gap-1.5 px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${
                        isActive || location.pathname.startsWith("/services")
                          ? "text-electric"
                          : "text-mist/80 hover:text-mist"
                      }`
                    }
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform duration-300 ${dropOpen ? "rotate-180" : ""}`} />
                  </NavLink>
                  <AnimatePresence>
                    {dropOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        data-testid="services-dropdown"
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[560px]"
                      >
                        <div className="bg-navy-light/95 backdrop-blur-xl border border-white/10 rounded-2xl p-6 grid grid-cols-2 gap-1 shadow-2xl shadow-black/50">
                          {SERVICES.map((s) => (
                            <button
                              key={s.slug}
                              data-testid={`dropdown-service-${s.slug}`}
                              onClick={() => navigate(`/services/${s.slug}`)}
                              className="text-left px-3 py-2.5 rounded-lg text-sm text-mist/75 hover:text-electric hover:bg-white/5 transition-colors duration-200"
                            >
                              {s.title}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-link`}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${
                      isActive ? "text-electric" : "text-mist/80 hover:text-mist"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="hidden lg:inline-flex items-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-300"
            >
              Let's Grow Your Brand
              <ArrowUpRight size={16} />
            </Link>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden text-mist p-2"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-50 bg-navy-deep flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-20">
              <span className="bg-white rounded-full px-3 py-1.5">
                <img src="/assets/logo.png" alt="Click World Online logo" className="h-8 w-auto" />
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMobileOpen(false)}
                className="text-mist p-2"
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((link, i) =>
                link.dropdown ? (
                  <div key={link.to}>
                    <div className="flex items-center justify-between">
                      <NavLink
                        to={link.to}
                        data-testid="mobile-nav-services"
                        className="font-display text-3xl font-bold text-mist py-3"
                      >
                        Services
                      </NavLink>
                      <button
                        data-testid="mobile-services-toggle"
                        onClick={() => setMobileServices(!mobileServices)}
                        className="text-electric p-3"
                        aria-label="Toggle services list"
                      >
                        <ChevronDown size={22} className={`transition-transform duration-300 ${mobileServices ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    <AnimatePresence>
                      {mobileServices && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-4 pb-4 flex flex-col gap-1 border-l border-white/10 ml-2">
                            {SERVICES.map((s) => (
                              <Link
                                key={s.slug}
                                to={`/services/${s.slug}`}
                                data-testid={`mobile-service-${s.slug}`}
                                className="py-2 text-mist/70 text-base hover:text-electric transition-colors"
                              >
                                {s.title}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                  >
                    <NavLink
                      to={link.to}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="font-display text-3xl font-bold text-mist py-3 block"
                    >
                      {link.label}
                    </NavLink>
                  </motion.div>
                )
              )}
              <Link
                to="/contact"
                data-testid="mobile-cta-button"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold px-8 py-4 rounded-full text-lg"
              >
                Let's Grow Your Brand
                <ArrowUpRight size={18} />
              </Link>
              <div className="mt-auto pt-10 flex items-center gap-4">
                <a href={COMPANY.instagram} target="_blank" rel="noopener noreferrer" data-testid="mobile-instagram" className="text-mist/60 hover:text-electric transition-colors" aria-label="Instagram">
                  <Instagram size={22} />
                </a>
                <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer" data-testid="mobile-facebook" className="text-mist/60 hover:text-electric transition-colors" aria-label="Facebook">
                  <Facebook size={22} />
                </a>
                <a href={`tel:${COMPANY.phone1.replace(/\s/g, "")}`} data-testid="mobile-phone" className="text-mist/60 text-sm ml-auto">
                  {COMPANY.phone1}
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
